import React, { useEffect, useState } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { LanguageProvider } from "./pages/LanguageContext";
import { ThemeProvider } from "./pages/theme";
import { NavigationProvider } from './constant/NavigationContext';
import { ModalProvider } from './constant/ModalContext';
import { sessionManager } from './utils/sessionManager';
import { MessengerProvider } from './pages/MessengerContext';
import ClientBadge from "./components/ClientBadge";

// Main Pages
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import UserProfile from "./pages/Userprofile";
import UserProfileAdmin from "./pages/Userprofileadmin";
import PasswordResetPage from './pages/PasswordResetPage';
import ProfessionalDashboardManager from './pages/DashboardManager';
import Mo3tiUserProfilemanager from './pages/UseProfileManager';
import DashboardSuper from "./pages/DashboardSuper";

// Role definitions
const ROLES = {
  ADMIN: "admin",
  MANAGER: "manager",
  USER: "user",
  SUPER: "superadmin"
};

// ✅ ENHANCED PrivateRoute with session management
function PrivateRoute({ children, allowedRoles = [] }) {
  const { user } = useAuth();

  // ✅ Check both session manager AND AuthContext
  const isAuthenticated = sessionManager.isAuthenticated();
  const userRole = sessionManager.getUserRole()?.toLowerCase();

  // ✅ If not authenticated in session manager, try to restore from storage
  if (!isAuthenticated) {
    // Try to get user from storage
    const storedUser = sessionManager.getUser();
    if (!storedUser) {
      return <Navigate to="/login" replace />;
    }
  }

  // ✅ Use userRole from session manager or user object as fallback
  const effectiveRole = userRole || user?.role?.toLowerCase();

  if (allowedRoles.length === 0) {
    return children;
  }

  const hasAccess = allowedRoles.some(role => role.toLowerCase() === effectiveRole);

  if (!hasAccess) {
    // ✅ Redirect to appropriate dashboard based on role
    if (effectiveRole === "user") {
      return <Navigate to="/userprofile" replace />;
    } else if (effectiveRole === "manager") {
      return <Navigate to="/DashboardManager" replace />;
    } else if (effectiveRole === "admin") {
      return <Navigate to="/dashboard" replace />;
    } else if (effectiveRole === "superadmin") {
      return <Navigate to="/DashboardSuper" replace />;
    }
    // If no role matches, redirect to login
    return <Navigate to="/login" replace />;
  }

  return children;
}

// ✅ ENHANCED Default Route Component
function DefaultRoute() {
  const { user } = useAuth();

  // ✅ Check both session manager AND user object
  const isAuthenticated = sessionManager.isAuthenticated();
  const userRole = (sessionManager.getUserRole() || user?.role)?.toLowerCase();

  // If not authenticated, redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Redirect based on role
  if (userRole === ROLES.ADMIN) {
    return <Navigate to="/dashboard/dashboard" replace />;
  } else if (userRole === ROLES.MANAGER) {
    return <Navigate to="/DashboardManager/dashboard" replace />;
  } else if (userRole === ROLES.USER) {
    return <Navigate to="/userprofile" replace />;
  } else if (userRole === ROLES.SUPER) {
    return <Navigate to="/DashboardSuper" replace />;
  }

  // Fallback to login if role is not recognized
  return <Navigate to="/login" replace />;
}

// Idle redirect component
function IdleRedirect({ to, idleTime = 300000 }) {
  const navigate = useNavigate();
  const [lastActivity, setLastActivity] = useState(Date.now());

  useEffect(() => {
    const updateActivity = () => setLastActivity(Date.now());

    window.addEventListener("mousemove", updateActivity);
    window.addEventListener("keydown", updateActivity);
    window.addEventListener("mousedown", updateActivity);
    window.addEventListener("touchstart", updateActivity);

    const interval = setInterval(() => {
      if (Date.now() - lastActivity > idleTime) {
        // Logout and cleanup session
        sessionManager.logout();
        navigate(to);
      }
    }, 1000);

    return () => {
      clearInterval(interval);
      window.removeEventListener("mousemove", updateActivity);
      window.removeEventListener("keydown", updateActivity);
      window.removeEventListener("mousedown", updateActivity);
      window.removeEventListener("touchstart", updateActivity);
    };
  }, [lastActivity, idleTime, navigate, to]);

  return null;
}

export default function App() {
  return (
    <AuthProvider>
      <ThemeProvider>
        <LanguageProvider>
          <MessengerProvider>
            <Router>
              <ModalProvider>
                <NavigationProvider>
                  <ClientBadge />
                  <IdleRedirect to="/login" idleTime={900000} />
                  <Routes>
                    {/* Public Routes */}
                    <Route path="/login" element={<Login />} />
                    <Route path="/reset-password" element={<PasswordResetPage />} />

                    {/* Admin Dashboard - Only admins */}
                    <Route
                      path="/dashboard/*"
                      element={
                        <PrivateRoute allowedRoles={[ROLES.ADMIN]}>
                          <Dashboard />
                        </PrivateRoute>
                      }
                    />

                    {/* Manager Dashboard - Only managers */}
                    <Route
                      path="/DashboardManager/*"
                      element={
                        <PrivateRoute allowedRoles={[ROLES.MANAGER]}>
                          <ProfessionalDashboardManager />
                        </PrivateRoute>
                      }
                    />

                    {/* User Profile - Only regular users */}
                    <Route
                      path="/userprofile/*"
                      element={
                        <PrivateRoute allowedRoles={[ROLES.USER]}>
                          <UserProfile />
                        </PrivateRoute>
                      }
                    />

                    {/* Admin viewing employee profile */}
                    <Route
                      path="/userprofileadmin/*"
                      element={
                        <PrivateRoute allowedRoles={[ROLES.ADMIN]}>
                          <UserProfileAdmin />
                        </PrivateRoute>
                      }
                    />

                    {/* Manager viewing employee profile */}
                    <Route
                      path="/userprofilemanager/*"
                      element={
                        <PrivateRoute allowedRoles={[ROLES.MANAGER]}>
                          <Mo3tiUserProfilemanager />
                        </PrivateRoute>
                      }
                    />

                    <Route
                      path="/DashboardSuper/*"
                      element={
                        <PrivateRoute allowedRoles={[ROLES.SUPER]}>
                          <DashboardSuper />
                        </PrivateRoute>
                      }
                    />

                    {/* Default Route - Redirects to appropriate dashboard based on role */}
                    <Route path="/" element={<DefaultRoute />} />

                    {/* Catch-all route - Redirects to default route */}
                    <Route path="*" element={<DefaultRoute />} />
                  </Routes>
                </NavigationProvider>
              </ModalProvider>
            </Router>
          </MessengerProvider>

        </LanguageProvider>
      </ThemeProvider>
    </AuthProvider>
  );
}